import MuleBadge from '../../components/MuleBadge';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { InformationCircleIcon, CheckCircleIcon, ClockIcon, BriefcaseIcon, TruckIcon, ExclamationTriangleIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import { useIconLegend } from '../../contexts/IconLegendContext';
import { LegendSection } from '../../components/IconLegendModal';
import GridLabel from '../../components/GridLabel';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import StatsGrid, { StatItem } from '../../components/StatsGrid';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';
import styles from './SafetyDriverScores.module.css';

// =============================================================================
// DRIVER SCORE WEIGHTS - Will be configurable via admin page later
// =============================================================================
const WEIGHT_ACCEPTANCE = 0.3;
const WEIGHT_ONTIME = 0.3;
const WEIGHT_WORKING = 0.25;
const PENALTY_FAILURE_RATE = 0.5; // Penalty multiplier for failure rate
const BONUS_PER_MOVE = 0.01; // Bonus points per completed move

// Calculate failure rate (failures per 100 moves)
const calculateFailureRate = (
  failures: number | null,
  totalMoves: number | null
): number | null => {
  if (totalMoves === null || totalMoves === 0) return null;
  const failureCount = failures ?? 0;
  return Math.round((failureCount / totalMoves) * 100 * 100) / 100; // Round to 2 decimals
};

// Calculate composite driver score using failure rate
const calculateDriverScore = (
  acceptanceRate: number | null,
  ontimeRate: number | null,
  workingRate: number | null,
  failureRate: number | null,
  totalMoves: number | null
): number => {
  const acceptance = acceptanceRate ?? 0;
  const ontime = ontimeRate ?? 0;
  const working = workingRate ?? 0;
  const failRate = failureRate ?? 0;
  const moves = totalMoves ?? 0;

  const score =
    (acceptance * WEIGHT_ACCEPTANCE) +
    (ontime * WEIGHT_ONTIME) +
    (working * WEIGHT_WORKING) -
    (failRate * PENALTY_FAILURE_RATE) +
    (moves * BONUS_PER_MOVE);

  return Math.max(0, Math.round(score * 100) / 100); // Round to 2 decimals, min 0
};

// Response from /driver-score/cached endpoint (all periods)
interface CachedDriverScoreSummary {
  unitnumber: string;
  isMule?: boolean;
  drivername: string | null;
  dispatcher: string | null;
  region: number | null;
  region_name: string | null;
  isdaycab: number | null;
  has93k: number | null;
  // 30 days
  service_failures_30_days: number | null;
  total_moves_30_days: number | null;
  acceptance_rate_30_days: number | null;
  ontime_rate_30_days: number | null;
  working_rate_30_days: number | null;
  estimated_pay_total_30_days: number | null;
  estimated_pay_per_dispatch_30_days: number | null;
  // 60 days
  service_failures_60_days: number | null;
  total_moves_60_days: number | null;
  acceptance_rate_60_days: number | null;
  ontime_rate_60_days: number | null;
  working_rate_60_days: number | null;
  estimated_pay_total_60_days: number | null;
  estimated_pay_per_dispatch_60_days: number | null;
  // 90 days
  service_failures_90_days: number | null;
  total_moves_90_days: number | null;
  acceptance_rate_90_days: number | null;
  ontime_rate_90_days: number | null;
  working_rate_90_days: number | null;
  estimated_pay_total_90_days: number | null;
  estimated_pay_per_dispatch_90_days: number | null;
  synced_at: string | null;
}

// Full cached score response from /driver-score/cached/{unitnumber}
interface CachedDriverScore {
  unitnumber: string;
  isMule?: boolean;
  drivername: string | null;
  service_failures: {
    all_time: number;
    '90_days': number;
    '60_days': number;
    '30_days': number;
  };
  acceptance: {
    all_time: { offered: number; accepted: number; rate: number | null };
    '90_days': { offered: number; accepted: number; rate: number | null };
    '60_days': { offered: number; accepted: number; rate: number | null };
    '30_days': { offered: number; accepted: number; rate: number | null };
  };
  ontime: {
    all_time: { total: number; delivered: number; rate: number | null };
    '90_days': { total: number; delivered: number; rate: number | null };
    '60_days': { total: number; delivered: number; rate: number | null };
    '30_days': { total: number; delivered: number; rate: number | null };
  };
  working: {
    all_time: { working_days: number; notworking_days: number; rate: number | null };
    '90_days': { working_days: number; notworking_days: number; rate: number | null };
    '60_days': { working_days: number; notworking_days: number; rate: number | null };
    '30_days': { working_days: number; notworking_days: number; rate: number | null };
  };
  estimated_pay?: {
    all_time: { total: number | null; per_dispatch: number | null };
    '90_days': { total: number | null; per_dispatch: number | null };
    '60_days': { total: number | null; per_dispatch: number | null };
    '30_days': { total: number | null; per_dispatch: number | null };
  };
  synced_at: string | null;
}

// Filter options response
interface DispatcherOption {
  username: string;
  displayname: string;
}

interface FilterOptions {
  dispatchers: DispatcherOption[];
  regions: { id: number; name: string }[];
}

// Combined stats for display (derived from selected period)
interface DisplayDriverStats {
  unitnumber: string;
  isMule?: boolean;
  drivername: string | null;
  dispatcher: string | null;
  region: number | null;
  region_name: string | null;
  isdaycab: number | null;
  has93k: number | null;
  service_failures: number | null;
  total_moves: number | null;
  failure_rate: number | null; // (failures / total_moves) * 100
  acceptance_rate: number | null;
  ontime_rate: number | null;
  working_rate: number | null;
  estimated_pay_total: number | null;
  estimated_pay_per_dispatch: number | null;
  synced_at: string | null;
  score: number; // Composite driver score (raw)
  normalizedScore: number; // Normalized score (rank 1 = 100)
}

type TablePeriod = '30_days' | '60_days' | '90_days';
type DetailPeriod = '30_days' | '60_days' | '90_days' | 'all_time';
// Tri-state truck attribute filter: show all, only true, or only false
type TriState = 'all' | 'true' | 'false';

const PERIOD_LABELS: Record<TablePeriod, string> = {
  '30_days': '30 Days',
  '60_days': '60 Days',
  '90_days': '90 Days',
};

// Render a 0/1 (or null) truck attribute as a compact Yes/No badge
const renderYesNo = (value: number | null) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 dark:text-gray-500">-</span>;
  }
  return value ? (
    <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
      Yes
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
      No
    </span>
  );
};

// Tri-state truck filter dropdown. Native <select> styled to match the
// MultiSelect trigger (same padding/border/ring + chevron) so the filter row
// reads as one consistent set of controls.
function TriStateFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: TriState;
  onChange: (value: TriState) => void;
}) {
  return (
    <div className="relative w-40">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as TriState)}
        className="appearance-none w-full cursor-pointer py-3 ps-4 pe-9 text-sm text-start bg-white border border-gray-400 rounded-lg text-heading focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:focus:ring-gray-600"
      >
        <option value="all">{label}: All</option>
        <option value="true">{label}: Yes</option>
        <option value="false">{label}: No</option>
      </select>
      <div className="pointer-events-none absolute top-1/2 end-3 -translate-y-1/2">
        <ChevronUpDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-500" />
      </div>
    </div>
  );
}

export default function SafetyDriverScores() {
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const [rawData, setRawData] = useState<CachedDriverScoreSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tablePeriod, setTablePeriod] = useState<TablePeriod>('30_days');
  const [detailPeriod, setDetailPeriod] = useState<DetailPeriod>('30_days');
  const [selectedDriverScore, setSelectedDriverScore] = useState<CachedDriverScore | null>(null);

  // Filter state
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ dispatchers: [], regions: [] });
  const [selectedDispatchers, setSelectedDispatchers] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<number[]>([]);
  const [daycabFilter, setDaycabFilter] = useState<TriState>('all');
  const [is93kFilter, setIs93kFilter] = useState<TriState>('all');
  const [highlightedDispatcher, setHighlightedDispatcher] = useState<string | null>(null);

  // Header-drag for the driver-details modal.
  const driverDetailsDrag = useModalDrag(!!selectedDriverScore);

  // Transform filter options to MultiSelect format
  const dispatcherOptions: MultiSelectOption[] = useMemo(() =>
    filterOptions.dispatchers.map(d => ({ value: d.username, label: d.displayname })),
    [filterOptions.dispatchers]
  );

  const regionOptions: MultiSelectOption[] = useMemo(() =>
    filterOptions.regions.map(r => ({ value: String(r.id), label: r.name })),
    [filterOptions.regions]
  );

  // Permissions mode context
  const { setPageComponents } = usePermissionsMode();
  const { setLegendSections } = useIconLegend();

  // Register component for permissions mode
  const pageComponentsList = useMemo(() => [
    { id: 'datatable-driver-scores', name: 'Driver Scores' },
  ], []);

  // Score calculation legend sections
  const scoreCalculationSections: LegendSection[] = useMemo(() => [
    {
      title: 'Score Formula',
      items: [
        {
          icon: <div className="text-2xl font-bold text-indigo-600">=</div>,
          title: 'Driver Score',
          description: 'Sum of weighted metrics: (Accept × 0.3) + (OnTime × 0.3) + (Working × 0.25) - (FailRate × 0.5) + (Moves × 0.01)',
        },
      ],
    },
    {
      title: 'Score Components',
      items: [
        {
          icon: <CheckCircleIcon className="h-6 w-6 text-green-500" />,
          title: 'Acceptance Rate (+30%)',
          description: 'Percentage of offered loads accepted. 95% acceptance = +28.5 points',
        },
        {
          icon: <ClockIcon className="h-6 w-6 text-blue-500" />,
          title: 'On-Time Rate (+30%)',
          description: 'Percentage of deliveries on time. 98% on-time = +29.4 points',
        },
        {
          icon: <BriefcaseIcon className="h-6 w-6 text-purple-500" />,
          title: 'Working Rate (+25%)',
          description: 'Percentage of weekdays available. 90% working = +22.5 points',
        },
        {
          icon: <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />,
          title: 'Failure Rate (-0.5 per %)',
          description: 'Service failures ÷ total moves × 100. Lower is better. 2% rate = -1.0 points',
        },
        {
          icon: <TruckIcon className="h-6 w-6 text-gray-500" />,
          title: 'Volume Bonus (+0.01/move)',
          description: 'Small bonus for completed moves. 100 moves = +1.0 points',
        },
      ],
    },
    {
      title: 'Example Calculation',
      items: [
        {
          icon: <div className="text-lg font-bold text-indigo-600">85</div>,
          title: 'Sample Driver Score',
          description: '95% accept (+28.5) + 98% on-time (+29.4) + 90% working (+22.5) - 2% failures (-1.0) + 100 moves (+1.0) = 80.4 pts',
        },
      ],
    },
  ], []);

  useEffect(() => {
    setPageComponents(pageComponentsList);
    setLegendSections(scoreCalculationSections, 'Score Calculation');

    // Clear components when unmounting
    return () => {
      setPageComponents([]);
      setLegendSections([]);
    };
  }, [pageComponentsList, setPageComponents, scoreCalculationSections, setLegendSections]);

  // Fetch filter options (only once on mount)
  const fetchFilterOptions = useCallback(async () => {
    try {
      // Fetch dispatchers and regions from safety endpoints
      const [dispatchersResponse, regionsResponse] = await Promise.all([
        api.get('/safety/dispatchers'),
        api.get('/safety/regions'),
      ]);

      // Dispatchers: keep username and displayname
      const dispatchers: DispatcherOption[] = dispatchersResponse.data.data;
      // Regions already in the right format: {id, name}
      const regions = regionsResponse.data.data;

      setFilterOptions({ dispatchers, regions });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, []);

  // Fetch driver scores with filters
  const fetchDriverStats = useCallback(async () => {
    try {
      setLoading(true);

      // Build query params for filters
      const params = new URLSearchParams();
      if (selectedDispatchers.length > 0) {
        params.append('dispatchers', selectedDispatchers.join(','));
      }
      if (selectedRegions.length > 0) {
        params.append('regions', selectedRegions.join(','));
      }
      // Tri-state truck filters — only sent when narrowed to true/false.
      // Server-side so they also reshape the StatsGrid and Top Drivers, not
      // just the table (e.g. is_93k=true -> "Global Top Drivers" = top 93k driver).
      if (daycabFilter !== 'all') {
        params.append('is_daycab', daycabFilter);
      }
      if (is93kFilter !== 'all') {
        params.append('is_93k', is93kFilter);
      }

      const queryString = params.toString();
      const url = queryString ? `/driver-score/cached?${queryString}` : '/driver-score/cached';

      const response = await api.get(url);
      // Handle both response formats: direct array or wrapped in { data: [...] }
      const cachedData: CachedDriverScoreSummary[] = Array.isArray(response.data)
        ? response.data
        : (response.data?.data || []);
      console.log('Driver scores loaded:', cachedData.length, 'drivers');
      setRawData(cachedData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching driver stats:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDispatchers, selectedRegions, daycabFilter, is93kFilter]);

  // Fetch full details for a specific driver
  const fetchDriverDetails = useCallback(async (unitnumber: string) => {
    try {
      const response = await api.get(`/driver-score/cached/${unitnumber}`);
      setSelectedDriverScore(response.data);
    } catch (err) {
      console.error('Error fetching driver details:', err);
    }
  }, []);

  // Fetch filter options on mount
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Fetch driver data when filters change
  useEffect(() => {
    fetchDriverStats();
  }, [fetchDriverStats]);

  // Transform data based on selected period (filtering is done server-side)
  const driverStats = useMemo(() => {
    // Transform to display format based on selected period
    const displayStats: DisplayDriverStats[] = rawData.map(driver => {
      const suffix = tablePeriod;
      const service_failures = driver[`service_failures_${suffix}` as keyof CachedDriverScoreSummary] as number | null;
      const total_moves = driver[`total_moves_${suffix}` as keyof CachedDriverScoreSummary] as number | null;
      const acceptance_rate = driver[`acceptance_rate_${suffix}` as keyof CachedDriverScoreSummary] as number | null;
      const ontime_rate = driver[`ontime_rate_${suffix}` as keyof CachedDriverScoreSummary] as number | null;
      const working_rate = driver[`working_rate_${suffix}` as keyof CachedDriverScoreSummary] as number | null;
      const estimated_pay_total = driver[`estimated_pay_total_${suffix}` as keyof CachedDriverScoreSummary] as number | null;
      const estimated_pay_per_dispatch = driver[`estimated_pay_per_dispatch_${suffix}` as keyof CachedDriverScoreSummary] as number | null;
      const failure_rate = calculateFailureRate(service_failures, total_moves);

      return {
        unitnumber: driver.unitnumber,
        drivername: driver.drivername,
        isMule: driver.isMule,
        dispatcher: driver.dispatcher,
        region: driver.region,
        region_name: driver.region_name,
        isdaycab: driver.isdaycab,
        has93k: driver.has93k,
        service_failures,
        total_moves,
        failure_rate,
        acceptance_rate,
        ontime_rate,
        working_rate,
        estimated_pay_total,
        estimated_pay_per_dispatch,
        synced_at: driver.synced_at,
        score: calculateDriverScore(acceptance_rate, ontime_rate, working_rate, failure_rate, total_moves),
        normalizedScore: 0, // Will be calculated after sorting
      };
    });

    // Sort by score descending (highest score = rank 1)
    const sortedStats = displayStats.sort((a, b) => b.score - a.score);

    // Normalize scores: rank 1 = 10000, others scaled proportionally
    const maxScore = sortedStats.length > 0 ? sortedStats[0].score : 1;
    if (maxScore > 0) {
      sortedStats.forEach(driver => {
        driver.normalizedScore = Math.round((driver.score / maxScore) * 10000); // Integer score, rank 1 = 10000
      });
    }

    return sortedStats;
  }, [rawData, tablePeriod]);

  // Get top 3 leaders
  // Filter driverStats by highlighted dispatcher for top drivers & table
  const filteredDriverStats = useMemo(() => {
    if (!highlightedDispatcher) return driverStats;
    return driverStats.filter(d => d.dispatcher === highlightedDispatcher);
  }, [driverStats, highlightedDispatcher]);

  const topLeaders = useMemo(() => filteredDriverStats.slice(0, 3), [filteredDriverStats]);

  // Compute dispatcher average scores for StatsGrid
  const dispatcherStats: StatItem[] = useMemo(() => {
    const grouped = new Map<string, { total: number; count: number }>();
    driverStats.forEach(d => {
      const disp = d.dispatcher || 'Unassigned';
      const entry = grouped.get(disp) || { total: 0, count: 0 };
      entry.total += d.normalizedScore;
      entry.count += 1;
      grouped.set(disp, entry);
    });

    // Find display name from filterOptions
    const displayNameMap = new Map(filterOptions.dispatchers.map(d => [d.username, d.displayname]));

    const sorted = Array.from(grouped.entries())
      .map(([dispatcher, { total, count }]) => ({
        id: `dispatcher-${dispatcher}`,
        label: (displayNameMap.get(dispatcher) || dispatcher) as React.ReactNode,
        value: Math.round(total / count).toLocaleString(),
        description: `${count} driver${count !== 1 ? 's' : ''}`,
        colorClass: 'text-indigo-600 dark:text-indigo-400',
        labelClass: '',
        onClick: () => {
          if (dispatcher === 'Unassigned') return;
          setHighlightedDispatcher(prev => prev === dispatcher ? null : dispatcher);
        },
        selected: dispatcher !== 'Unassigned' && highlightedDispatcher === dispatcher,
      }))
      .sort((a, b) => {
        const aVal = parseInt(String(a.value).replace(/,/g, ''), 10);
        const bVal = parseInt(String(b.value).replace(/,/g, ''), 10);
        return bVal - aVal;
      });

    // Style highest and lowest
    if (sorted.length > 1) {
      sorted[0].labelClass = 'text-yellow-600 dark:text-yellow-400 font-bold';
      sorted[sorted.length - 1].labelClass = 'text-red-800 dark:text-red-500 italic';
    }

    // Add rank badges based on which dispatcher has the #1, #2, #3 individual driver
    const rankBadges = [
      <span key="g" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 text-yellow-900 text-[0.625rem] font-bold ml-1 align-middle">1</span>,
      <span key="s" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700 dark:from-gray-500 dark:to-gray-600 dark:text-gray-100 text-[0.625rem] font-bold ml-1 align-middle">2</span>,
      <span key="b" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900 text-[0.625rem] font-bold ml-1 align-middle">3</span>,
    ];
    const top3Drivers = driverStats.slice(0, 3);
    top3Drivers.forEach((driver, i) => {
      const dispKey = driver.dispatcher || 'Unassigned';
      const item = sorted.find(s => s.id === `dispatcher-${dispKey}`);
      if (item) {
        const name = item.label;
        item.label = <>{name} {rankBadges[i]}</>;
      }
    });

    return sorted;
  }, [driverStats, filterOptions.dispatchers, highlightedDispatcher]);

  const getRateColor = (rate: number | null) => {
    if (rate === null) return 'text-muted';
    if (rate >= 90) return 'text-green-600 dark:text-green-400';
    if (rate >= 75) return 'text-yellow-600 dark:text-yellow-400';
    if (rate >= 60) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const formatRate = (rate: number | null) => {
    if (rate === null) return '-';
    return `${rate.toFixed(1)}%`;
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };


  // Dynamic title for Top Drivers section
  const topDriversTitle = useMemo(() => {
    if (highlightedDispatcher) {
      const displayNameMap = new Map(filterOptions.dispatchers.map(d => [d.username, d.displayname]));
      const name = displayNameMap.get(highlightedDispatcher) || highlightedDispatcher;
      return `${name}'s Top Drivers`;
    }
    return 'Global Top Drivers';
  }, [highlightedDispatcher, filterOptions.dispatchers]);

  // Active header-level filters, summarized for the Top Drivers notice so it's
  // clear what population the leaders/ranking are drawn from (regions + truck attrs).
  const activeFilters = useMemo(() => {
    const chips: string[] = [];
    if (selectedRegions.length > 0) {
      const nameMap = new Map(filterOptions.regions.map(r => [r.id, r.name]));
      const names = selectedRegions.map(id => nameMap.get(id) || `Region ${id}`);
      chips.push(`Region${names.length > 1 ? 's' : ''}: ${names.join(', ')}`);
    }
    if (daycabFilter !== 'all') chips.push(`Daycab: ${daycabFilter === 'true' ? 'Yes' : 'No'}`);
    if (is93kFilter !== 'all') chips.push(`93k: ${is93kFilter === 'true' ? 'Yes' : 'No'}`);
    return chips;
  }, [selectedRegions, filterOptions.regions, daycabFilter, is93kFilter]);

  // Column definitions for TanTable
  const columns = useMemo<ColumnDef<DisplayDriverStats>[]>(() => [
    {
      id: 'rank',
      header: 'Rank',
      enableSorting: false,
      cell: ({ row }) => {
        const rank = row.index + 1;
        return (
          <span className="font-semibold text-gray-600 dark:text-gray-300">
            {rank}
          </span>
        );
      },
    },
    {
      accessorKey: 'unitnumber',
      header: 'Driver',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = parseInt(rowA.original.unitnumber, 10);
        const b = parseInt(rowB.original.unitnumber, 10);
        if (isNaN(a) && isNaN(b)) return rowA.original.unitnumber.localeCompare(rowB.original.unitnumber);
        if (isNaN(a)) return 1;
        if (isNaN(b)) return -1;
        return a - b;
      },
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-heading">{row.original.drivername || row.original.unitnumber}</span>
          <span className="text-xs text-muted">Unit {row.original.unitnumber}<MuleBadge isMule={row.original.isMule} /></span>
        </div>
      ),
    },
    {
      accessorKey: 'dispatcher',
      header: 'Dispatcher',
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue<string | null>();
        const displayNameMap = new Map(filterOptions.dispatchers.map(d => [d.username, d.displayname]));
        return (
          <span className="text-label">
            {value ? (displayNameMap.get(value) || value) : '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'isdaycab',
      header: 'Daycab',
      enableSorting: true,
      sortingFn: (rowA, rowB) => (rowA.original.isdaycab ?? -1) - (rowB.original.isdaycab ?? -1),
      cell: ({ getValue }) => renderYesNo(getValue<number | null>()),
    },
    {
      accessorKey: 'has93k',
      header: '93k',
      enableSorting: true,
      sortingFn: (rowA, rowB) => (rowA.original.has93k ?? -1) - (rowB.original.has93k ?? -1),
      cell: ({ getValue }) => renderYesNo(getValue<number | null>()),
    },
    {
      accessorKey: 'normalizedScore',
      header: 'Score',
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue<number>();
        return (
          <span className="font-bold text-indigo-600 dark:text-indigo-400">
            {value.toLocaleString()}
          </span>
        );
      },
    },
    {
      accessorKey: 'total_moves',
      header: 'Total Moves',
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        return (
          <span className="text-heading font-medium">
            {value ?? 0}
          </span>
        );
      },
    },
    {
      accessorKey: 'service_failures',
      header: 'Service Failures',
      enableSorting: true,
      cell: ({ row }) => {
        const count = row.original.service_failures;
        const rate = row.original.failure_rate;
        const hasFailures = count !== null && count > 0;
        return (
          <div className="flex flex-col">
            <span className={hasFailures ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-gray-300'}>
              {count ?? 0}
            </span>
            <span className="text-xs text-muted">
              {rate !== null ? `${rate.toFixed(1)}%` : '-'}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'acceptance_rate',
      header: 'Acceptance Rate',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.acceptance_rate ?? -1;
        const b = rowB.original.acceptance_rate ?? -1;
        return a - b;
      },
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        return (
          <span className={`font-medium ${getRateColor(value)}`}>
            {formatRate(value)}
          </span>
        );
      },
    },
    {
      accessorKey: 'ontime_rate',
      header: 'On-Time Rate',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.ontime_rate ?? -1;
        const b = rowB.original.ontime_rate ?? -1;
        return a - b;
      },
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        return (
          <span className={`font-medium ${getRateColor(value)}`}>
            {formatRate(value)}
          </span>
        );
      },
    },
    {
      accessorKey: 'working_rate',
      header: 'Working Rate',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.working_rate ?? -1;
        const b = rowB.original.working_rate ?? -1;
        return a - b;
      },
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        return (
          <span className={`font-medium ${getRateColor(value)}`}>
            {formatRate(value)}
          </span>
        );
      },
    },
    {
      accessorKey: 'estimated_pay_total',
      header: 'Total Pay',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.estimated_pay_total ?? -1;
        const b = rowB.original.estimated_pay_total ?? -1;
        return a - b;
      },
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        return (
          <span className="font-medium text-green-600 dark:text-green-400">
            {formatCurrency(value)}
          </span>
        );
      },
    },
    {
      accessorKey: 'estimated_pay_per_dispatch',
      header: 'Avg/Dispatch',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.estimated_pay_per_dispatch ?? -1;
        const b = rowB.original.estimated_pay_per_dispatch ?? -1;
        return a - b;
      },
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        return (
          <span className="font-medium text-green-600 dark:text-green-400">
            {formatCurrency(value)}
          </span>
        );
      },
    },
    {
      id: 'details',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            fetchDriverDetails(row.original.unitnumber);
          }}
          className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          title="View Details"
        >
          <InformationCircleIcon className="h-5 w-5" />
        </button>
      ),
    },
  ], [fetchDriverDetails, formatRate, getRateColor]);

  // Rank badge colors
  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
            <span className="text-yellow-600 dark:text-yellow-400 font-bold">1</span>
          </div>
        );
      case 2:
        return (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-600">
            <span className="text-gray-600 dark:text-gray-300 font-bold">2</span>
          </div>
        );
      case 3:
        return (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
            <span className="text-amber-700 dark:text-amber-400 font-bold">3</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      <div className={`${styles.body} px-4 sm:px-6 lg:px-8 py-8`} style={{ position: 'relative' }}>
        <GridLabel areaName="body" position="top-right" />

        {/* Period Toggle & Filters */}
        <div className="flex items-center justify-between mb-6">
          {/* Left-side filters */}
          <div className="flex items-center gap-3">
            {/* Region Filter */}
            <div className="w-48">
              <MultiSelect
                options={regionOptions}
                value={selectedRegions.map(String)}
                onChange={(values) => setSelectedRegions(values.map(v => parseInt(v, 10)))}
                placeholder="All Regions"
                enableSearch
                searchPlaceholder="Search regions..."
                showClearOption
                clearOptionLabel="All Regions"
              />
            </div>

            {/* Daycab Filter */}
            <TriStateFilter label="Daycab" value={daycabFilter} onChange={setDaycabFilter} />

            {/* 93k Filter */}
            <TriStateFilter label="93k" value={is93kFilter} onChange={setIs93kFilter} />
          </div>

          {/* Period Toggle */}
          <div className="flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
            {(['30_days', '60_days', '90_days'] as TablePeriod[]).map((period) => (
              <button
                key={period}
                onClick={() => setTablePeriod(period)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  tablePeriod === period
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {PERIOD_LABELS[period]}
              </button>
            ))}
          </div>
        </div>

        {/* Dispatchers StatsGrid */}
        {!loading && !error && dispatcherStats.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-medium text-heading mb-4">
              Dispatchers
            </h2>
            <StatsGrid
              stats={dispatcherStats}
              columns={Math.min(dispatcherStats.length, 6)}
              rows={Math.ceil(dispatcherStats.length / 6)}
              size="sm"
            />
          </div>
        )}

        {/* Top Drivers Section */}
        {!loading && !error && topLeaders.length > 0 && (
          <div className="mb-8">
            <div className="mb-4">
              <h2 className="text-lg font-medium text-heading">
                {topDriversTitle}
              </h2>
              {activeFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className="text-xs text-muted">Filtered by:</span>
                  {activeFilters.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Leader Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topLeaders.map((leader, index) => (
                <div
                  key={leader.unitnumber}
                  className="bg-panel rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
                >
                  {/* Top section: Rank Circle, Name/Unit, Score */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {/* Rank Circle */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl ${
                        index === 0
                          ? 'bg-gradient-to-br from-yellow-300 to-yellow-500 text-yellow-900'
                          : index === 1
                          ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700 dark:from-gray-500 dark:to-gray-600 dark:text-gray-100'
                          : 'bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-heading">
                          {leader.drivername || `Driver ${leader.unitnumber}`}
                        </p>
                        <p className="text-sm text-muted">
                          Unit {leader.unitnumber}<MuleBadge isMule={leader.isMule} />
                          {leader.dispatcher && (
                            <span className="ml-2 text-gray-400 dark:text-gray-500">
                              &middot; {filterOptions.dispatchers.find(d => d.username === leader.dispatcher)?.displayname || leader.dispatcher}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {/* Score on right */}
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {leader.normalizedScore.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted">pts</p>
                    </div>
                  </div>

                  {/* Stats rows */}
                  <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3">
                    {/* Row 1: Accept, On-Time, Failures */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-xs font-medium text-muted uppercase tracking-wide">
                          Accept
                        </p>
                        <p className={`text-lg font-semibold ${getRateColor(leader.acceptance_rate)}`}>
                          {formatRate(leader.acceptance_rate)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-medium text-muted uppercase tracking-wide">
                          On-Time
                        </p>
                        <p className={`text-lg font-semibold ${getRateColor(leader.ontime_rate)}`}>
                          {formatRate(leader.ontime_rate)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-medium text-muted uppercase tracking-wide">
                          Failures
                        </p>
                        <p className={`text-lg font-semibold ${leader.service_failures && leader.service_failures > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          {leader.service_failures ?? 0}
                        </p>
                      </div>
                    </div>
                    {/* Row 2: Total Pay, Avg/Dispatch, Moves */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-xs font-medium text-muted uppercase tracking-wide">
                          Total Pay
                        </p>
                        <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(leader.estimated_pay_total)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-medium text-muted uppercase tracking-wide">
                          Avg/Dispatch
                        </p>
                        <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(leader.estimated_pay_per_dispatch)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-medium text-muted uppercase tracking-wide">
                          Moves
                        </p>
                        <p className="text-lg font-semibold text-heading">
                          {leader.total_moves ?? 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error loading driver scores
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading driver scores...</p>
          </div>
        )}

        {/* Global Ranking Table */}
        {!loading && !error && filteredDriverStats.length > 0 && (
          <div>
            <h2 className="text-lg font-medium text-heading mb-4">
              Global Ranking
            </h2>
            <TanTable
              data={filteredDriverStats}
              columns={columns}
              loading={loading}
              settingsKey="driver-scores"
              searchPlaceholder="Search by driver name..."
              defaultPageSize={25}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && driverStats.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted">No driver scores found.</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Try adjusting your filters or check back later.
            </p>
          </div>
        )}

      {/* Driver Details Modal */}
      {selectedDriverScore && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-scrim/60 transition-opacity" aria-hidden="true" onClick={() => setSelectedDriverScore(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div
              ref={driverDetailsDrag.panelRef}
              style={driverDetailsDrag.panelStyle}
              className="inline-block align-bottom bg-panel rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
            >
              <ModalHeader
                title={`Driver ${selectedDriverScore.unitnumber}${selectedDriverScore.drivername ? ` - ${selectedDriverScore.drivername}` : ''} - Detailed Metrics`}
                titleId="modal-title"
                onClose={() => setSelectedDriverScore(null)}
                dragHandleProps={driverDetailsDrag.dragHandleProps}
              />
              <div className="bg-panel px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="text-center sm:text-left w-full">
                    {/* Period Selector */}
                    <div className="flex space-x-2">
                      {(['30_days', '60_days', '90_days', 'all_time'] as DetailPeriod[]).map((period) => (
                        <button
                          key={period}
                          onClick={() => setDetailPeriod(period)}
                          className={`px-3 py-1 text-sm rounded-md ${
                            detailPeriod === period
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-label'
                          }`}
                        >
                          {period === 'all_time' ? 'All Time' : period.replace('_', ' ')}
                        </button>
                      ))}
                    </div>

                    {/* Metrics Grid */}
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-muted">Service Failures</h4>
                        <p className="mt-1 text-2xl font-semibold text-red-600 dark:text-red-400">
                          {selectedDriverScore.service_failures[detailPeriod]}
                        </p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-muted">Acceptance Rate</h4>
                        <p className={`mt-1 text-2xl font-semibold ${getRateColor(selectedDriverScore.acceptance[detailPeriod].rate)}`}>
                          {formatRate(selectedDriverScore.acceptance[detailPeriod].rate)}
                        </p>
                        <p className="text-xs text-muted mt-1">
                          {selectedDriverScore.acceptance[detailPeriod].accepted} / {selectedDriverScore.acceptance[detailPeriod].offered} accepted
                        </p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-muted">On-Time Rate</h4>
                        <p className={`mt-1 text-2xl font-semibold ${getRateColor(selectedDriverScore.ontime[detailPeriod].rate)}`}>
                          {formatRate(selectedDriverScore.ontime[detailPeriod].rate)}
                        </p>
                        <p className="text-xs text-muted mt-1">
                          {selectedDriverScore.ontime[detailPeriod].delivered} / {selectedDriverScore.ontime[detailPeriod].total} on-time
                        </p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-muted">Working Rate</h4>
                        <p className={`mt-1 text-2xl font-semibold ${getRateColor(selectedDriverScore.working[detailPeriod].rate)}`}>
                          {formatRate(selectedDriverScore.working[detailPeriod].rate)}
                        </p>
                        <p className="text-xs text-muted mt-1">
                          {selectedDriverScore.working[detailPeriod].working_days} working / {selectedDriverScore.working[detailPeriod].notworking_days} not working
                        </p>
                      </div>

                      {selectedDriverScore.estimated_pay && (
                        <>
                          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                            <h4 className="text-sm font-medium text-muted">Total Pay</h4>
                            <p className="mt-1 text-2xl font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(selectedDriverScore.estimated_pay[detailPeriod].total)}
                            </p>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                            <h4 className="text-sm font-medium text-muted">Avg Per Dispatch</h4>
                            <p className="mt-1 text-2xl font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(selectedDriverScore.estimated_pay[detailPeriod].per_dispatch)}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {selectedDriverScore.synced_at && (
                      <p className="mt-4 text-xs text-muted">
                        Last synced: {new Date(selectedDriverScore.synced_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setSelectedDriverScore(null)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}
